/** Board relationship kinds a player can switch off individually. */
export type BoardRelationshipKind =
  "pin" | "relative_pin" | "skewer" | "fork" | "hanging" | "support" | "xray" | "discovered_potential" | "check";
export const BOARD_RELATIONSHIP_KINDS: readonly BoardRelationshipKind[] = [
  "pin",
  "relative_pin",
  "skewer",
  "fork",
  "hanging",
  "support",
  "xray",
  "discovered_potential",
  "check",
];

export interface BoardPreferences {
  evaluationPreset: "quick" | "balanced" | "deep";
  useMaiaReplies: boolean;
  showLegalMoves: boolean;
  showMotifs: boolean;
  showPrincipleSignals: boolean;
  showOpponentAnalysis: boolean;
  showBestMoveArrow: boolean;
  showPositionMarkers: boolean;
  markerPins: boolean;
  markerSupport: boolean;
  /** Relationship kinds hidden from board arrows and badges; absent in settings saved before it existed. */
  hiddenRelationships?: BoardRelationshipKind[];
  showAllPositionMarkers: boolean;
  moveSounds: boolean;
  showEvaluation: boolean;
  showTacticalMotifs: boolean;
}

export interface UserSettings extends BoardPreferences {
  reviewMode: "guided" | "open";
  gameBoardSettings: BoardPreferences | null;
  freeBoardSettings: BoardPreferences | null;
  patternBoardSettings: BoardPreferences | null;
  nikolaBoardSettings: BoardPreferences | null;
  rememberPageState: boolean;
  animationsEnabled: boolean;
  gamesDefaultFilter: "all" | "analyzed";
  gamesDefaultFormat: "all" | "rapid" | "blitz" | "bullet" | "daily";
  gamesDefaultClock: string | null;
  gamesDefaultSort: {
    key: "result" | "opponent" | "rating" | "date" | "opening" | "accuracy";
    direction: "asc" | "desc";
  } | null;
  patternsDefaultFormat: "rapid" | "blitz" | "bullet" | "daily" | null;
  trendsDefaultFormat: "rapid" | "blitz" | "bullet" | "daily" | null;
  patternsDefaultClock: string | null;
  trendsDefaultClock: string | null;
  patternsDefaultPeriod: "7d" | "30d" | "90d" | "1y" | "all";
  trendsDefaultPeriod: "7d" | "30d" | "90d" | "1y" | "all";
  analysisDefaultPreset: "quick" | "balanced" | "deep";
  analysisDefaultFormat: "all" | "rapid" | "blitz" | "bullet" | "daily";
  analysisDefaultClock: string | null;
  performanceScopeCollapsed: boolean;
  crossSessionMemoryEnabled: boolean;
  nikolaMemoryRetentionDays: 30 | 90 | 365;
  nikolaChatRetentionDays: "forever" | 30 | 90 | 365;
  automaticWebResearchEnabled: boolean;
  nikolaInstructions: string;
}

export type UserSettingsPatch = Partial<UserSettings>;
export const NIKOLA_INSTRUCTIONS_MAX_LENGTH = 2_000;

export interface UserSettingsResponse {
  settings: UserSettings;
  updatedAt: string | null;
}

export const DEFAULT_BOARD_PREFERENCES: BoardPreferences = {
  evaluationPreset: "balanced",
  useMaiaReplies: false,
  showLegalMoves: true,
  showMotifs: true,
  showPrincipleSignals: true,
  showOpponentAnalysis: true,
  showBestMoveArrow: true,
  showPositionMarkers: true,
  markerPins: true,
  markerSupport: true,
  hiddenRelationships: [],
  showAllPositionMarkers: false,
  moveSounds: true,
  showEvaluation: true,
  showTacticalMotifs: true,
};

export const DEFAULT_USER_SETTINGS: UserSettings = {
  ...DEFAULT_BOARD_PREFERENCES,
  reviewMode: "guided",
  gameBoardSettings: null,
  freeBoardSettings: { ...DEFAULT_BOARD_PREFERENCES, evaluationPreset: "quick" },
  patternBoardSettings: { ...DEFAULT_BOARD_PREFERENCES, showOpponentAnalysis: false },
  nikolaBoardSettings: null,
  rememberPageState: true,
  animationsEnabled: true,
  gamesDefaultFilter: "all",
  gamesDefaultFormat: "all",
  gamesDefaultClock: null,
  gamesDefaultSort: null,
  patternsDefaultFormat: null,
  trendsDefaultFormat: null,
  patternsDefaultClock: null,
  trendsDefaultClock: null,
  patternsDefaultPeriod: "all",
  trendsDefaultPeriod: "all",
  analysisDefaultPreset: "balanced",
  analysisDefaultFormat: "all",
  analysisDefaultClock: null,
  performanceScopeCollapsed: false,
  crossSessionMemoryEnabled: true,
  nikolaMemoryRetentionDays: 365,
  nikolaChatRetentionDays: "forever",
  automaticWebResearchEnabled: true,
  nikolaInstructions: "",
};
